import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';

export interface SystemCheck {
  name: string;
  status: 'success' | 'error' | 'warning' | 'loading';
  message: string;
  details?: string;
  category: 'backend' | 'frontend' | 'database' | 'security' | 'ui';
}

interface SystemCheckCardProps {
  check: SystemCheck;
}

const SystemCheckCard: React.FC<SystemCheckCardProps> = ({ check }) => {
  const getStatusIcon = (status: SystemCheck['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'loading':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
    }
  };

  const getStatusBadge = (status: SystemCheck['status']) => {
    const variants = {
      success: 'default',
      error: 'destructive',
      warning: 'secondary',
      loading: 'outline'
    } as const;

    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  return (
    <div className="flex items-start gap-3 p-3 border rounded-lg">
      {getStatusIcon(check.status)}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-medium">{check.name}</h4>
          {getStatusBadge(check.status)}
        </div>
        <p className="text-sm text-muted-foreground mb-1">{check.message}</p>
        {check.details && (
          <p className="text-xs text-muted-foreground">{check.details}</p>
        )}
      </div>
    </div>
  );
};

export default SystemCheckCard;
